import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import sh from "bundles/common/utils/sh";
import DateRangeSelect from "./common/DateRangeSelect";
import { T, cardSx } from "./platform_analytics/PlatformAnalyticsShared";
import OverviewTab from "./platform_analytics/OverviewTab";
import CompanyHealthTab from "./platform_analytics/CompanyHealthTab";
import FunnelsTab from "./platform_analytics/FunnelsTab";
import CandidateSupplyTab from "./platform_analytics/CandidateSupplyTab";
import TalentTeamTab from "./platform_analytics/TalentTeamTab";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "custom", label: "Custom" },
  { key: "total", label: "Total" },
];

const TABS = [
  { key: "overview", label: "Overview", endpoint: "overview", Component: OverviewTab },
  { key: "company_health", label: "Company health", endpoint: "company_health", Component: CompanyHealthTab },
  { key: "funnels", label: "Funnels", endpoint: "funnels", Component: FunnelsTab },
  { key: "candidate_supply", label: "Candidate supply", endpoint: "candidate_supply", Component: CandidateSupplyTab },
  { key: "talent_team", label: "Talent team", endpoint: "talent_team", Component: TalentTeamTab },
];

const PlatformAnalyticsPage = () => {
  const [period, setPeriod] = useState("month");
  const [customRange, setCustomRange] = useState({ startDate: null, endDate: null });
  const [activeTab, setActiveTab] = useState(0);
  // Per-tab cache so switching tabs/periods only refetches what's needed.
  const [cache, setCache] = useState({});
  const [loadingTab, setLoadingTab] = useState(null);

  const params = useMemo(() => {
    const p = { period };
    if (period === "custom" && customRange.startDate && customRange.endDate) {
      p.start_date = customRange.startDate;
      p.end_date = customRange.endDate;
    }
    return p;
  }, [period, customRange]);

  // Cache key resets whenever the time window changes.
  const paramKey = useMemo(() => JSON.stringify(params), [params]);

  const tab = TABS[activeTab];

  const fetchTab = useCallback((endpoint) => {
    const key = `${endpoint}:${paramKey}`;
    if (cache[key]) return;
    setLoadingTab(endpoint);
    sh.get(`/internal/platform_analytics/${endpoint}`, { params })
      .then((res) => setCache((prev) => ({ ...prev, [key]: res.data })))
      .catch(() => {})
      .finally(() => setLoadingTab((cur) => (cur === endpoint ? null : cur)));
  }, [cache, paramKey, params]);

  useEffect(() => {
    if (period === "custom" && (!customRange.startDate || !customRange.endDate)) return;
    fetchTab(tab.endpoint);
  }, [tab.endpoint, paramKey, period, customRange, fetchTab]);

  const awaitingCustomDates = period === "custom" && (!customRange.startDate || !customRange.endDate);
  const data = cache[`${tab.endpoint}:${paramKey}`];
  const isLoading = !awaitingCustomDates && !data && loadingTab === tab.endpoint;
  const TabComponent = tab.Component;

  return (
    <Box sx={{ backgroundColor: T.bg, minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", px: "32px", py: "32px", display: "flex", flexDirection: "column", gap: "24px" }}>
        {/* Header + period selector */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography component="h1" sx={{ fontSize: 28, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.fg, lineHeight: 1.2 }}>
              Platform analytics
            </Typography>
            <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, mt: "6px" }}>
              Habits, health and outcomes across every company on the platform
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            {period === "custom" && (
              <DateRangeSelect
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={(r) => setCustomRange(r)}
              />
            )}
            <Box sx={{ display: "flex", backgroundColor: T.bg3, borderRadius: "8px", p: "2px", border: `1px solid ${T.border}` }}>
              {PERIODS.map((p) => (
                <Box
                  key={p.key}
                  component="button"
                  onClick={() => setPeriod(p.key)}
                  sx={{
                    all: "unset",
                    px: "16px",
                    py: "6px",
                    fontSize: 12,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all 150ms ease",
                    color: period === p.key ? T.fg : T.dim,
                    backgroundColor: period === p.key ? T.bg2 : "transparent",
                    boxShadow: period === p.key ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                    "&:hover": { color: T.fg },
                  }}
                >
                  {p.label}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Tab bar */}
        <Box sx={{ display: "flex", gap: "4px", borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
          {TABS.map((tb, i) => (
            <Box
              key={tb.key}
              component="button"
              onClick={() => setActiveTab(i)}
              sx={{
                all: "unset",
                px: "16px",
                py: "10px",
                fontSize: 13,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                cursor: "pointer",
                color: activeTab === i ? T.fg : T.dim,
                borderBottom: activeTab === i ? `2px solid ${T.primary}` : "2px solid transparent",
                marginBottom: "-1px",
                transition: "color 150ms ease",
                "&:hover": { color: T.fg },
              }}
            >
              {tb.label}
            </Box>
          ))}
        </Box>

        {/* Tab content */}
        {awaitingCustomDates ? (
          <Box sx={{ ...cardSx, py: 6, textAlign: "center" }}>
            <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: T.fg }}>
              Pick a date range
            </Typography>
            <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: T.dim, mt: 0.5 }}>
              Choose a start and end date above to load the custom-range analytics.
            </Typography>
          </Box>
        ) : isLoading || !data ? (
          <Stack spacing={2}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={96} />
              ))}
            </Box>
            <Skeleton variant="rounded" height={300} />
          </Stack>
        ) : (
          <TabComponent data={data} />
        )}
      </Box>
    </Box>
  );
};

export default PlatformAnalyticsPage;
